import type {
  CentinelaCallPackage,
  CentinelaLicenseLevel,
  CentinelaResourcePlan,
  CentinelaSubscriptionCatalog,
  CentinelaSubscriptionPlan,
  CentinelaUsageAmount,
  CentinelaUsageApplication,
  CentinelaUsageBlock,
  CentinelaUsageBreakdown,
  CentinelaUsageByType,
  CentinelaUsageGroup,
  CentinelaUsageMetric,
  CentinelaUsageProjection,
  CentinelaUsageTopUnit,
  CentinelaUsageVoice,
} from "../../../features/centinela/types";

type Unknown = Record<string, unknown>;

const APPLICATIONS: CentinelaUsageApplication[] = ["soft", "hard", "metric"];

const asRecord = (value: unknown): Unknown =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Unknown) : {};

const asList = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const asText = (value: unknown) => (typeof value === "string" ? value : "");

const asBoolean = (value: unknown) => value === true;

const asNumber = (value: unknown, fallback = 0) => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const asNullableNumber = (value: unknown) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const asApplication = (value: unknown): CentinelaUsageApplication => {
  const text = asText(value) as CentinelaUsageApplication;
  return APPLICATIONS.includes(text) ? text : "metric";
};

function mapProjection(value: unknown): CentinelaUsageProjection | null {
  if (!value || typeof value !== "object") return null;
  const row = asRecord(value);
  return {
    cierre_estimado: asNumber(row.cierre_estimado),
    avance_periodo_pct: asNumber(row.avance_periodo_pct),
    confiable: asBoolean(row.confiable),
  };
}

function mapMetric(value: unknown): CentinelaUsageMetric | null {
  if (!value || typeof value !== "object") return null;
  const row = asRecord(value);
  const label = asRecord(row.etiqueta);
  const singular = asText(label.singular);
  const plural = asText(label.plural) || singular;

  return {
    kind: asText(row.kind),
    etiqueta: { singular, plural },
    usado: asNumber(row.usado),
    tope: asNullableNumber(row.tope),
    tope_origen: row.tope_origen === null ? null : asText(row.tope_origen) || null,
    todo_facturable: asBoolean(row.todo_facturable),
    restante: asNullableNumber(row.restante),
    pct: asNullableNumber(row.pct),
    extra: asNumber(row.extra),
    importe_extra: asNumber(row.importe_extra),
    precio_unitario: asNumber(row.precio_unitario),
    aplicacion: asApplication(row.aplicacion),
    cobrable: asBoolean(row.cobrable),
    aviso: row.aviso === null ? null : asText(row.aviso) || null,
    proyeccion: mapProjection(row.proyeccion),
  };
}

function mapAmount(value: unknown): CentinelaUsageAmount {
  const row = asRecord(value);
  return {
    base: asNumber(row.base),
    extra: asNumber(row.extra),
    total: asNumber(row.total),
    proyectado_cierre: asNumber(row.proyectado_cierre),
  };
}

function mapGroups(value: unknown): CentinelaUsageGroup[] {
  return asList(value).map((item) => {
    const row = asRecord(item);
    return {
      id: asText(row.id),
      etiqueta: asText(row.etiqueta),
      descripcion: asText(row.descripcion),
      llamadas: asNumber(row.llamadas),
      pct: asNumber(row.pct),
    };
  });
}

function mapByType(value: unknown): CentinelaUsageByType[] {
  return asList(value).map((item) => {
    const row = asRecord(item);
    return {
      tipo: asText(row.tipo),
      llamadas: asNumber(row.llamadas),
      grupo: asText(row.grupo),
    };
  });
}

function mapVoice(value: unknown): CentinelaUsageVoice | null {
  if (!value || typeof value !== "object") return null;
  const row = asRecord(value);
  return {
    minutos_totales: asNumber(row.minutos_totales),
    promedio_por_llamada: asText(row.promedio_por_llamada),
    por_dia: asList(row.por_dia).map((item) => {
      const day = asRecord(item);
      return { dia: asText(day.dia), minutos: asNumber(day.minutos) };
    }),
  };
}

function mapTopUnits(value: unknown): CentinelaUsageTopUnit[] {
  return asList(value).map((item) => {
    const row = asRecord(item);
    return {
      device_id: asText(row.device_id),
      nombre: asText(row.nombre),
      llamadas: asNumber(row.llamadas),
      tiempo: asText(row.tiempo),
      pct: asNumber(row.pct),
    };
  });
}

export function mapCentinelaUsage(payload: unknown): {
  usage: CentinelaUsageBlock;
  breakdown: CentinelaUsageBreakdown;
  hasData: boolean;
} {
  const root = asRecord(payload);
  const block = asRecord(root.usage);
  const desglose = asRecord(root.desglose);

  return {
    hasData: Boolean(root.usage),
    usage: {
      company_id: asText(block.company_id),
      period: asText(block.period),
      modelo: asText(block.modelo),
      moneda: asText(block.moneda) || "MXN",
      precio_base: asNumber(block.precio_base),
      compromiso: asNullableNumber(block.compromiso),
      avance_periodo_pct: asNumber(block.avance_periodo_pct),
      protagonista: mapMetric(block.protagonista),
      secundarios: asList(block.secundarios)
        .map(mapMetric)
        .filter((metric): metric is CentinelaUsageMetric => metric !== null),
      importe: mapAmount(block.importe),
    },
    breakdown: {
      total: asNumber(desglose.total),
      grupos: mapGroups(desglose.grupos),
      por_tipo: mapByType(desglose.por_tipo),
      voz: mapVoice(desglose.voz),
      top_unidades: mapTopUnits(desglose.top_unidades),
    },
  };
}

function mapResources(value: unknown): Record<string, CentinelaResourcePlan> {
  const row = asRecord(value);
  return Object.entries(row).reduce<Record<string, CentinelaResourcePlan>>(
    (acc, [name, plan]) => {
      const item = asRecord(plan);
      return {
        ...acc,
        [name]: {
          incluido: asNullableNumber(item.incluido),
          precio_unitario: asNumber(item.precio_unitario),
          aplicacion: asApplication(item.aplicacion),
        },
      };
    },
    {},
  );
}

function mapLicenseLevels(value: unknown): CentinelaLicenseLevel[] {
  const row = asRecord(value);
  return Object.entries(row).map(([id, level]) => {
    const item = asRecord(level);
    return {
      id,
      llamadas_incluidas: asNumber(item.llamadas_incluidas),
      geocercas: asNullableNumber(item.geocercas),
      viajes: asNullableNumber(item.viajes),
      precio_unidad_mes: asNumber(item.precio_unidad_mes),
      precio_llamada_extra: asNullableNumber(item.precio_llamada_extra),
    };
  });
}

function mapCallPackages(value: unknown): CentinelaCallPackage[] {
  return asList(value).map((item) => {
    const row = asRecord(item);
    const calls = asNumber(row.llamadas);
    const price = asNumber(row.precio);
    return {
      llamadas: calls,
      precio: price,
      unitario: asNumber(row.unitario, calls > 0 ? price / calls : 0),
    };
  });
}

export function mapCentinelaSubscription(payload: unknown): {
  plan: CentinelaSubscriptionPlan;
  catalog: CentinelaSubscriptionCatalog;
  hasPlan: boolean;
} {
  const root = asRecord(payload);
  const plan = asRecord(root.subscription);
  const catalog = asRecord(root.catalog);

  return {
    hasPlan: Boolean(root.subscription) && !asBoolean(plan.unconfigured),
    plan: {
      model: asText(plan.model),
      level: plan.level === null ? null : asText(plan.level) || null,
      currency: asText(plan.currency) || "MXN",
      base_price: asNumber(plan.base_price),
      resources: mapResources(plan.resources),
      unconfigured: asBoolean(plan.unconfigured),
    },
    catalog: {
      models: asList(catalog.models).map((item) => asText(item)),
      license_levels: mapLicenseLevels(catalog.license_levels),
      call_packages: mapCallPackages(catalog.call_packages),
    },
  };
}
